import type { ViewController } from '@/engine/app/View.controller';
import { TYPES } from '@/engine/ioc';
import { CallbackManager, type CallbackSubscription } from '@/engine/Utilities/CallbackManager';
import { DisposablesBasket } from '@/engine/Utilities/DisposablesBasket';
import { safeResource } from '@/engine/Utilities/IDisposable';
import { inject, injectable } from 'inversify';
import { noRecursionScope } from '../../engine/Utilities/FunctorsUtils';
import { TYPES as ImagesTYPES } from '@/engine/app/modules/images/iocTypes';
import type { ImagesService } from '@/engine/app/modules/images/Images.service';
import { AutoDisposer } from '@/engine/Utilities/AutoDisposer';
import { reaction } from 'mobx';
import { ImagesMode } from '@/engine/app/modules/images/interfaces';
import { AutoContext } from '@/engine/Utilities/AutoContext';
import type { ViewsService } from '@/engine/app/Views.service';
import { ViewPortType } from '@/engine/app/interfaces';

@injectable()
export class SynchronizeViewsController {
  private readonly _viewsService: ViewsService;

  private readonly _viewController: ViewController;

  private readonly _imagesService: ImagesService;

  private readonly _onViewsSyncStateChangedListeners = new CallbackManager<
    (activeViewDescriptor: string | undefined) => void
  >();

  private _disposables = new DisposablesBasket();

  public constructor(
    @inject(TYPES.ViewsService) viewsService: ViewsService,
    @inject(TYPES.ViewController) viewController: ViewController,
    @inject(ImagesTYPES.ImagesService) imagesService: ImagesService
  ) {
    this._viewsService = viewsService;
    this._viewController = viewController;
    this._imagesService = imagesService;
    reaction(
      () => viewsService.currentViewOrThrow.syncViewsDescriptor,
      (descriptor): void => {
        this._onViewSyncTriggered(descriptor);
        this._onViewsSyncStateChangedListeners.invoke(descriptor);
      }
    );
  }

  public triggerViewsSync(viewDescriptor: string): void {
    let activeViewDescriptor = this._viewsService.currentViewOrThrow.syncViewsDescriptor;
    if (activeViewDescriptor === viewDescriptor) {
      activeViewDescriptor = undefined;
    } else {
      activeViewDescriptor = viewDescriptor;
    }
    this._viewsService.currentViewOrThrow.setSyncViewsDescriptor(activeViewDescriptor);
  }

  public isViewsSyncActive(viewDescriptor: string): boolean {
    return this._viewsService.currentViewOrThrow.syncViewsDescriptor === viewDescriptor;
  }

  public subscribeToViewsSyncStateChanged(
    handler: (activeViewDescriptor: string | undefined) => void
  ): CallbackSubscription {
    return safeResource(this._onViewsSyncStateChangedListeners.subscribe(handler), () => {
      handler(this._viewsService.currentViewOrThrow.syncViewsDescriptor);
    });
  }

  private _onViewSyncTriggered(descriptor: string | undefined): void {
    this._disposables.dispose();
    if (descriptor) {
      const imagesController = this._viewController.getViewPortController(descriptor);
      const threeDController = this._viewController.getViewPortController(ViewPortType.ThreeD);

      if (!imagesController || !threeDController) {
        throw new Error('Logic Error');
      }

      const imagesCamera = imagesController.getCameraController();
      const threeDCamera = threeDController.getCameraController();

      const scope = noRecursionScope();

      this._disposables.add(
        imagesCamera.onCameraModified(() => scope(() => threeDController.synchronizeCamera(imagesCamera)))
      );
      this._disposables.add(
        threeDCamera.onCameraModified(() => scope(() => imagesController.synchronizeCamera(threeDCamera)))
      );
      this._disposables.add(
        new AutoDisposer(
          reaction(
            () => this._imagesService.imagesMode,
            (mode) => {
              // Disable 3D view camera rotations in Centerline mode
              threeDController.setNoRotateCamera(mode == ImagesMode.POLYLINE);
            }
          )
        )
      );
      this._disposables.add(
        new AutoContext(
          () => {
            if (this._imagesService.imagesMode == ImagesMode.POLYLINE) {
              threeDController.setNoRotateCamera(true);
            }
          },
          () => {
            // always explicitly unlock rotations
            threeDController.setNoRotateCamera(false);
          }
        )
      );
      this._disposables.add(
        new AutoDisposer(
          reaction(
            () => this._viewsService.currentView,
            () => {
              // always synchronize Images to 3D on steps change
              scope(() => threeDController.synchronizeCamera(imagesCamera));
            }
          )
        )
      );

      scope(() => threeDController.synchronizeCamera(imagesCamera));
    }
  }
}
